import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DialogActions from "@mui/material/DialogActions";

import ModalBasicLayout from "../ModalBasicLayout";
import Loader from "../../Loaders/Loader";
import Gallery from "../../Gallery/Gallery";
import LinearLoaderWithTime from "../../Loaders/LinearLoaderWithTime";
import Select, { SELECT_MENU_ABOVE_MODAL_PROPS } from "../../Form/Select";
import Button from "../../Buttons/Button";
import { BUTTON_SHADOW } from "../../../utils/colors";
import { IS_ADLE_DOMAIN } from "../../../utils/constants";
import { AdleLogoLoader } from "../../../assets/svg/AdleLogoLoader";
import {
  BUTTON_ADD_SELECTED_CONTENT,
  MODAL_GALLERY_ARIA_LABEL,
  MODAL_META_AD_LIBRARY_LOADING_FETCHING_ADS,
  MODAL_META_AD_LIBRARY_LOADING_PREPARING_GALLERY,
  MODAL_META_AD_LIBRARY_LOADING_RESOLVING_COMPETITORS,
  PROCESSING_MEDIA_TO_USE,
  SAVING_MEDIA,
} from "../../../i18n/keysTranslations";
import {
  COMPETITOR_FILTER_BAR_SX,
  COMPETITOR_FILTER_SELECT_WRAPPER_SX,
  COMPETITOR_PLATFORM_FILTER_SELECT_WRAPPER_SX,
  META_AD_LIBRARY_COMPETITOR_FILTER_FIELD,
  META_AD_LIBRARY_PLATFORM_FILTER_FIELD,
  META_AD_LIBRARY_LOADING_TIME_MS,
} from "./constants";

const MODAL_GALLERY_TITLE_SX = {
  justifyContent: "space-between",
  width: "100%",
  pl: 9,
  pb: 2,
};

const renderPlatformFilterValue = ({ value, options }) => {
  const selectedOption = options.find((option) => option.value === value);

  if (!selectedOption) return "";

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {selectedOption.iconSrc ? (
        <Box
          component="img"
          src={selectedOption.iconSrc}
          alt=""
          sx={{
            width: 16,
            height: 16,
            objectFit: "contain",
            display: "block",
            flexShrink: 0,
          }}
        />
      ) : null}
      {selectedOption.label}
    </Box>
  );
};

const ModalGalleryFromUrlLayout = ({
  t,
  modalOpen,
  onCloseModal,
  titleKey,
  isLoading,
  loadingMessageKey,
  error,
  totalTopOffsetPx,
  emptyContentKey,
  isMetaAdLibraryMode,
  showCompetitorFilter,
  competitorFilterControl,
  competitorFilterOptions,
  platformFilterOptions = [],
  onPlatformFilterChange = () => {},
  competitorFilterAccessory = null,
  competitorFilterSelectSx = {},
  competitorFilterValidationChange = async () => true,
  galleryDisplayData,
  galleryProps,
  hasSelection,
  isUploading,
  onConfirmSelection,
  showUploadingFooter = false,
  uploadingFooterLabels = [],
  emptyContentKeyOverride = null,
}) => {
  const hasModalTitle = Boolean(titleKey);
  const sxTitle = hasModalTitle ? MODAL_GALLERY_TITLE_SX : undefined;

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      title={hasModalTitle ? t(titleKey) : undefined}
      titleContent={!hasModalTitle}
      sxTitle={sxTitle}
      fullWidth={true}
      fullScreen
      scroll="paper"
      aria-label={t(MODAL_GALLERY_ARIA_LABEL)}
      sxContent={{
        p: 0,
        pl: 3,
        pb: 2,
        mt: 1,
        overflowX: "hidden",
        "::-webkit-scrollbar": {
          width: 0,
          height: 0,
          background: "transparent",
        },
      }}
    >
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            px: 4,
            py: 6,
            minHeight: `calc(100vh - ${totalTopOffsetPx + 120}px)`,
          }}
        >
          {isMetaAdLibraryMode ? (
            <Box
              sx={{
                width: "100%",
                maxWidth: 640,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
              }}
            >
              {IS_ADLE_DOMAIN && <AdleLogoLoader />}
              <LinearLoaderWithTime
                labelSide={IS_ADLE_DOMAIN ? "bottom" : "top"}
                time={META_AD_LIBRARY_LOADING_TIME_MS}
                labels={[
                  t(MODAL_META_AD_LIBRARY_LOADING_RESOLVING_COMPETITORS),
                  t(MODAL_META_AD_LIBRARY_LOADING_FETCHING_ADS),
                  t(MODAL_META_AD_LIBRARY_LOADING_PREPARING_GALLERY),
                ]}
                sx={{ width: "100%" }}
              />
            </Box>
          ) : (
            <Loader
              size={70}
              hasMessage={true}
              message={t(loadingMessageKey)}
            />
          )}
        </Box>
      )}

      {!isLoading && !!error && (
        <Box
          sx={{
            height: `calc(100% - ${totalTopOffsetPx}px)`,
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ml: "-12px",
            px: 2,
          }}
        >
          <Typography variant="body1" align="center" color="error.main">
            {error}
          </Typography>
        </Box>
      )}

      {showCompetitorFilter ? (
        <Box sx={COMPETITOR_FILTER_BAR_SX}>
          <Box sx={COMPETITOR_PLATFORM_FILTER_SELECT_WRAPPER_SX}>
            <Select
              control={competitorFilterControl}
              name={META_AD_LIBRARY_PLATFORM_FILTER_FIELD}
              options={platformFilterOptions}
              rules={{ required: false }}
              doTranslate={false}
              fullWidth
              MenuProps={SELECT_MENU_ABOVE_MODAL_PROPS}
              variant="filled"
              sx={competitorFilterSelectSx}
              actionsChange={onPlatformFilterChange}
              renderValue={(value) =>
                renderPlatformFilterValue({
                  value,
                  options: platformFilterOptions,
                })
              }
            />
          </Box>
          <Box sx={COMPETITOR_FILTER_SELECT_WRAPPER_SX}>
            <Select
              control={competitorFilterControl}
              name={META_AD_LIBRARY_COMPETITOR_FILTER_FIELD}
              options={competitorFilterOptions}
              rules={{ required: false }}
              doTranslate={false}
              displayEmpty
              fullWidth
              MenuProps={SELECT_MENU_ABOVE_MODAL_PROPS}
              variant="filled"
              sx={competitorFilterSelectSx}
              validationChange={competitorFilterValidationChange}
            />
          </Box>
          {competitorFilterAccessory}
        </Box>
      ) : null}

      {!isLoading && !error && galleryDisplayData.length > 0 && (
        <Gallery {...galleryProps} />
      )}

      {!isLoading && !error && galleryDisplayData.length === 0 && (
        <Box
          sx={{
            height: `calc(100% - ${totalTopOffsetPx}px)`,
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ml: "-12px",
          }}
        >
          <Typography variant="h4" align="center">
            {t(emptyContentKeyOverride || emptyContentKey)}
          </Typography>
        </Box>
      )}

      {(hasSelection || (showUploadingFooter && isUploading)) && (
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: isUploading ? "#FFF" : "transparent",
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            mt: 2,
            mb: 2,
            py: 1,
          }}
        >
          {isUploading && (isMetaAdLibraryMode || showUploadingFooter) ? (
            <LinearLoaderWithTime
              labels={
                showUploadingFooter && uploadingFooterLabels.length > 0
                  ? uploadingFooterLabels.map((labelKey) => t(labelKey))
                  : [t(PROCESSING_MEDIA_TO_USE), t(SAVING_MEDIA)]
              }
              time={30 * 1000}
            />
          ) : (
            <Button
              color="primary"
              disabled={isUploading}
              sx={
                isMetaAdLibraryMode
                  ? {
                      boxShadow: BUTTON_SHADOW,
                      "&:hover": {
                        boxShadow: BUTTON_SHADOW,
                      },
                    }
                  : undefined
              }
              onClick={(e) => {
                e?.preventDefault();
                e?.stopPropagation();
                onConfirmSelection();
              }}
            >
              {t(BUTTON_ADD_SELECTED_CONTENT)}
            </Button>
          )}
        </DialogActions>
      )}
    </ModalBasicLayout>
  );
};

export default ModalGalleryFromUrlLayout;
